import { convert } from 'html-to-text';
import TurndownService from 'turndown';

const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '•'
});

export const exportToFormat = async (content, format, formData = {}) => {
  const timestamp = new Date().toISOString().split('T')[0];
  const jobTitle = formData?.jobTitle || 'Job-Posting';
  const baseFilename = `${jobTitle.replace(/[^a-zA-Z0-9]/g, '-')}-${timestamp}`;

  switch (format) {
    case 'html':
      return {
        content: convertToHTML(content),
        filename: `${baseFilename}.html`,
        mimeType: 'text/html'
      };
    case 'markdown':
      return {
        content: content, // Already in markdown-like format
        filename: `${baseFilename}.md`,
        mimeType: 'text/markdown'
      };
    case 'text':
      return {
        content: convertToPlainText(content),
        filename: `${baseFilename}.txt`,
        mimeType: 'text/plain'
      };
    case 'indeed':
      return {
        content: convertToIndeedFormat(content, formData),
        filename: `${baseFilename}-indeed.txt`,
        mimeType: 'text/plain'
      };
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
};

export const exportInterviewQuestions = async (questions, format, formData = {}) => {
  const timestamp = new Date().toISOString().split('T')[0];
  const jobTitle = formData?.jobTitle || 'Position';
  const baseFilename = `${jobTitle.replace(/[^a-zA-Z0-9]/g, '-')}-Interview-Questions-${timestamp}`;

  const content = formatInterviewQuestions(questions, format);

  switch (format) {
    case 'clipboard':
      return {
        content: content,
        mimeType: 'text/plain'
      };
    case 'pdf':
      return {
        content: generateInterviewPDF(questions, formData),
        filename: `${baseFilename}.html`,
        mimeType: 'text/html'
      };
    case 'docx':
      return {
        content: generateInterviewWord(questions, formData),
        filename: `${baseFilename}.html`,
        mimeType: 'text/html'
      };
    case 'text':
      return {
        content: content,
        filename: `${baseFilename}.txt`,
        mimeType: 'text/plain'
      };
    default:
      return {
        content: content,
        filename: `${baseFilename}.txt`,
        mimeType: 'text/plain'
      };
  }
};

const formatInterviewQuestions = (questions, format = 'text') => {
  const { categorizedQuestions } = questions;
  const jobTitle = questions.jobTitle || 'Position';
  
  let content = `INTERVIEW QUESTIONS\n`;
  content += `Position: ${jobTitle}\n`;
  content += `Generated: ${new Date().toLocaleDateString()}\n`;
  content += `Total Questions: ${questions.totalQuestions}\n\n`;
  content += `${'='.repeat(50)}\n\n`;

  Object.entries(categorizedQuestions).forEach(([category, categoryQuestions]) => {
    if (categoryQuestions.length === 0) return;
    
    content += `${category.toUpperCase().replace('-', ' ')} QUESTIONS\n`;
    content += `${'-'.repeat(30)}\n\n`;
    
    categoryQuestions.forEach((question, index) => {
      content += `${index + 1}. ${question}\n\n`;
    });
    
    content += `\n`;
  });

  content += `${'='.repeat(50)}\n`;
  content += `Instructions for Interviewers:\n`;
  content += `• Allow 45-60 minutes for the complete interview\n`;
  content += `• Take notes on candidate responses\n`;
  content += `• Ask follow-up questions for clarification\n`;
  content += `• Ensure all candidates receive similar questions\n`;
  content += `• Focus on specific examples and measurable outcomes\n\n`;
  
  content += `Scoring Guidelines:\n`;
  content += `• Excellent (4): Comprehensive answer with specific examples\n`;
  content += `• Good (3): Clear answer with some examples\n`;
  content += `• Satisfactory (2): Basic answer, limited examples\n`;
  content += `• Needs Improvement (1): Unclear or incomplete answer\n\n`;

  return content;
};

const generateInterviewPDF = (questions, formData) => {
  const content = formatInterviewQuestions(questions);
  const jobTitle = formData?.jobTitle || 'Position';
  
  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Interview Questions - ${jobTitle}</title>
    <style>
        body {
            font-family: 'Arial', sans-serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px 20px;
            color: #333;
        }
        .header {
            text-align: center;
            border-bottom: 3px solid #3b82f6;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        .header h1 {
            color: #1e40af;
            margin-bottom: 10px;
            font-size: 24px;
        }
        .meta-info {
            background: #f8fafc;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 30px;
            border-left: 4px solid #3b82f6;
        }
        .category {
            margin-bottom: 30px;
            page-break-inside: avoid;
        }
        .category-title {
            color: #1e40af;
            font-size: 18px;
            font-weight: bold;
            border-bottom: 2px solid #e5e7eb;
            padding-bottom: 5px;
            margin-bottom: 15px;
            text-transform: uppercase;
        }
        .question {
            margin-bottom: 20px;
            padding: 15px;
            background: #fafafa;
            border-radius: 6px;
            border-left: 3px solid #3b82f6;
        }
        .question-number {
            font-weight: bold;
            color: #3b82f6;
        }
        .instructions {
            background: #ecfdf5;
            border: 1px solid #d1fae5;
            border-radius: 8px;
            padding: 20px;
            margin-top: 40px;
        }
        .instructions h3 {
            color: #065f46;
            margin-bottom: 15px;
        }
        .instructions ul {
            margin: 10px 0;
            padding-left: 20px;
        }
        .instructions li {
            margin: 5px 0;
        }
        @media print {
            body { padding: 20px; }
            .category { page-break-inside: avoid; }
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>Interview Questions</h1>
        <p>Position: ${jobTitle}</p>
        <p>City of Metropolis</p>
    </div>

    <div class="meta-info">
        <strong>Generated:</strong> ${new Date().toLocaleDateString()}<br>
        <strong>Total Questions:</strong> ${questions.totalQuestions}<br>
        <strong>Categories:</strong> ${questions.categories.join(', ')}
    </div>

    ${Object.entries(questions.categorizedQuestions).map(([category, categoryQuestions]) => 
      categoryQuestions.length > 0 ? `
      <div class="category">
          <h2 class="category-title">${category.replace('-', ' ')} Questions</h2>
          ${categoryQuestions.map((question, index) => `
              <div class="question">
                  <span class="question-number">${index + 1}.</span> ${question}
              </div>
          `).join('')}
      </div>` : ''
    ).join('')}

    <div class="instructions">
        <h3>Instructions for Interviewers</h3>
        <ul>
            <li>Allow 45-60 minutes for the complete interview</li>
            <li>Take detailed notes on candidate responses</li>
            <li>Ask follow-up questions for clarification</li>
            <li>Ensure all candidates receive similar questions</li>
            <li>Focus on specific examples and measurable outcomes</li>
        </ul>

        <h3>Scoring Guidelines</h3>
        <ul>
            <li><strong>Excellent (4):</strong> Comprehensive answer with specific examples</li>
            <li><strong>Good (3):</strong> Clear answer with some examples</li>
            <li><strong>Satisfactory (2):</strong> Basic answer, limited examples</li>
            <li><strong>Needs Improvement (1):</strong> Unclear or incomplete answer</li>
        </ul>
    </div>
</body>
</html>`;
};

const generateInterviewWord = (questions, formData) => {
  // For Word format, we'll generate HTML that can be easily converted
  return generateInterviewPDF(questions, formData);
};

const convertToHTML = (content) => {
  // Convert markdown-like content to HTML
  let html = content
    // Convert headers
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    // Convert bold text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Convert bullet points
    .replace(/^• (.+)$/gm, '<li>$1</li>')
    // Convert line breaks
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');

  // Wrap bullet points in ul tags
  html = html.replace(/(<li>.*<\/li>)/gs, (match) => {
    return `<ul>${match}</ul>`;
  });

  // Wrap content in paragraphs
  html = `<p>${html}</p>`;

  // Clean up empty paragraphs and fix structure
  html = html
    .replace(/<p><\/p>/g, '')
    .replace(/<p>(<h[1-6]>)/g, '$1')
    .replace(/(<\/h[1-6]>)<\/p>/g, '$1')
    .replace(/<p>(<ul>)/g, '$1')
    .replace(/(<\/ul>)<\/p>/g, '$1');

  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Job Posting</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 20px; color: #333; }
        h1 { color: #2c3e50; border-bottom: 2px solid #3498db; padding-bottom: 10px; }
        h2 { color: #34495e; margin-top: 30px; }
        h3 { color: #7f8c8d; }
        ul { margin: 10px 0; }
        li { margin: 5px 0; }
        strong { color: #2c3e50; }
    </style>
</head>
<body>
    ${html}
</body>
</html>`;
};

const convertToPlainText = (content) => {
  // Remove markdown formatting for plain text
  return content
    .replace(/^#{1,6} /gm, '') // Remove header markers
    .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold markers
    .replace(/^• /gm, '- ') // Convert bullet points
    .trim();
};

const convertToIndeedFormat = (content, formData) => {
  // Indeed-specific formatting
  let indeedContent = convertToPlainText(content);
  
  // Add Indeed-specific structure
  const sections = indeedContent.split('\n\n');
  let formattedSections = [];

  sections.forEach(section => {
    if (section.includes('About the Role') || section.includes('Job Summary')) {
      formattedSections.push(`JOB SUMMARY:\n${section.replace(/About the Role|Job Summary/g, '').trim()}`);
    } else if (section.includes('Responsibilities') || section.includes('Key Responsibilities')) {
      formattedSections.push(`RESPONSIBILITIES:\n${section.replace(/.*Responsibilities/g, '').trim()}`);
    } else if (section.includes('Qualifications') || section.includes('Requirements')) {
      formattedSections.push(`QUALIFICATIONS:\n${section.replace(/.*Qualifications|.*Requirements/g, '').trim()}`);
    } else if (section.includes('Benefits') || section.includes('What We Offer')) {
      formattedSections.push(`BENEFITS:\n${section.replace(/.*Benefits|.*What We Offer/g, '').trim()}`);
    } else if (!section.includes('How to Apply') && !section.includes('Generated using')) {
      formattedSections.push(section);
    }
  });

  return formattedSections.join('\n\n').trim();
};

export default { exportToFormat, exportInterviewQuestions };